import { getMealItemKey } from '../combo';
import { IOrderProduct, IOrderProductComboItem, IOrderProductExclusion, IOrderProductModifier, IOrderProductModifierOption } from '~/types/orders';
import { PRODUCT_TYPE } from '~/types/product';
import { hash } from '~/utils/formatter';

interface IModifierOptionHash {
  id: number;
  quantity: number;
  price: number;
}

type TModifiersHash = Record<string, { options: IModifierOptionHash[] }>;

type TComboItemHash = Record<string, { modifiers: TModifiersHash; exclusions: number[] }>;

/**
 * for a unique React key
 */
export const getOrderProductHash = (product: IOrderProduct): string => {
  const isMealCombo: boolean = product.type === PRODUCT_TYPE.MEAL_COMBO;

  const json = {
    id: product.id,
    price: product.price,
    options: _orderProductModifiersHash(product.modifiers),
    exclusions: _orderProductExclusionsHash(product.exclusions),
    items: _orderProductComboItemsHash(product.items, isMealCombo),
    size: product.size ? product.size : null,
    notes: product.notes ? product.notes : null,
  };

  return hash(JSON.stringify(json));
};

const _orderProductModifiersHash = (modifiers: IOrderProductModifier[]): TModifiersHash => {
  if (!modifiers?.length) return {};

  return modifiers.reduce((sum: TModifiersHash, current) => {
    if (!current.options?.length) sum[String(current.id)] = { options: [] };

    sum[String(current.id)] = {
      options: _orderProductModifierOptionsHash(current.options),
    };

    return sum;
  }, {});
};

const _orderProductModifierOptionsHash = (options: IOrderProductModifierOption[] | undefined): IModifierOptionHash[] => {
  if (!options?.length) return [];

  return options.map((option) => ({ id: option.id, quantity: option.quantity, price: option.price }));
};

const _orderProductExclusionsHash = (exclusions: IOrderProductExclusion[] | undefined): number[] => {
  if (!exclusions?.length) return [];

  return exclusions.map((exclusion) => exclusion.id);
};

const _orderProductComboItemsHash = (items: IOrderProductComboItem[] | undefined, isMealCombo: boolean): TComboItemHash | number[] => {
  if (!items) return [];

  if (isMealCombo) {
    return items.reduce((sum: TComboItemHash, current) => {
      sum[getMealItemKey(current)] = {
        modifiers: _orderProductModifiersHash(current.modifiers),
        // TODO: combo exclusions
        // exclusions: _exclusionsHash(current.exclusions),
        exclusions: [],
      };

      return sum;
    }, {});
  }

  return items.map((item) => item.id);
};
